import { useCallback, useEffect, useRef, useState, type MouseEventHandler } from 'react';
import { availability, availabilityAccessibleName } from './availability';
import { faqs, photoMatchTrust, productFacts } from './data';
import { useReducedMotion } from './motion';
import { LiteralBrandMark, Wordmark } from './components/BrandMark';
import { INVITE_CODE_DIALOG_ID, InviteCodeDialog } from './components/InviteCodeDialog';
import { MobileNav } from './components/MobileNav';
import { ProductStory } from './components/ProductStory';
import { PhoneStage } from './components/PhoneStage';

type InviteRequestHandler = MouseEventHandler<HTMLButtonElement>;

function StickyMobileCta({ onInviteRequest }: { onInviteRequest: InviteRequestHandler }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!('IntersectionObserver' in window)) return;

    const hero = document.getElementById('top');
    const availabilitySection = document.getElementById('availability');
    const footer = document.getElementById('site-footer');
    if (!hero || !availabilitySection || !footer) return;

    const nodes = [hero, availabilitySection, footer];
    const intersections = new Map<Element, boolean>();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => intersections.set(entry.target, entry.isIntersecting));
        setVisible(
          intersections.get(hero) === false &&
          !intersections.get(availabilitySection) &&
          !intersections.get(footer),
        );
      },
      { threshold: 0.04 },
    );

    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, []);

  return (
    <aside
      className={`sticky-mobile-cta ${visible ? 'is-visible' : ''}`}
      aria-hidden={!visible}
      aria-label="Sticket availability"
    >
      <span>Private testing</span>
      <button
        type="button"
        data-invite-cta="sticky-mobile"
        aria-haspopup="dialog"
        aria-controls={INVITE_CODE_DIALOG_ID}
        tabIndex={visible ? 0 : -1}
        onClick={onInviteRequest}
      >
        {availability.primaryLabel}<span aria-hidden="true">↗</span>
      </button>
    </aside>
  );
}

function App() {
  const reducedMotion = useReducedMotion();
  const mainRef = useRef<HTMLElement>(null);
  const inviteTriggerRef = useRef<HTMLButtonElement>(null);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);

  const openInviteDialog: InviteRequestHandler = useCallback((event) => {
    inviteTriggerRef.current = event.currentTarget;
    setInviteDialogOpen(true);
  }, []);

  const closeInviteDialog = useCallback(() => setInviteDialogOpen(false), []);

  useEffect(() => {
    if (reducedMotion) {
      document.documentElement.style.setProperty('--page-progress', '0');
      return;
    }

    let frame = 0;
    const update = () => {
      frame = 0;
      const max = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
      document.documentElement.style.setProperty('--page-progress', String(Math.min(1, window.scrollY / max)));
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [reducedMotion]);

  return (
    <>
      <a
        className="skip-link"
        href="#main"
        onClick={() => mainRef.current?.focus({ preventScroll: true })}
      >
        Skip to content
      </a>
      <header className="site-nav">
        <a className="brand-link" href="#top" aria-label="Sticket home">
          <LiteralBrandMark className="compact-brand-mark" priority />
          <Wordmark />
        </a>
        <nav aria-label="Primary navigation">
          <a href="#product">Product</a>
          <a href="#trust">Trust</a>
          <a href="#faq">FAQ</a>
        </nav>
        <button
          className="nav-status"
          type="button"
          data-invite-cta="desktop-nav"
          aria-haspopup="dialog"
          aria-controls={INVITE_CODE_DIALOG_ID}
          onClick={openInviteDialog}
        >
          {availability.primaryLabel.toUpperCase()}
        </button>
        <MobileNav
          inviteDialogOpen={inviteDialogOpen}
          onInviteRequest={openInviteDialog}
        />
      </header>

      <main id="main" ref={mainRef} tabIndex={-1}>
        <section className="hero" id="top" aria-labelledby="hero-title">
          <div className="hero-light hero-light-left" aria-hidden="true" />
          <div className="hero-light hero-light-right" aria-hidden="true" />
          <div className="hero-content layout-bound">
            <div className="hero-copy">
              <LiteralBrandMark className="hero-brand-mark" priority />
              <p className="eyebrow">Your live-events life</p>
              <h1 id="hero-title">
                <span className="hero-title-line">Keep the night.</span>
                <span className="hero-title-line hero-title-line-accent">Lose the noise.</span>
              </h1>
              <p className="hero-lede">
                The show, the seat, the people — and what comes next.
              </p>
              <div className="hero-actions">
                <button
                  className="primary-button"
                  type="button"
                  data-invite-cta="hero"
                  aria-haspopup="dialog"
                  aria-controls={INVITE_CODE_DIALOG_ID}
                  onClick={openInviteDialog}
                >
                  {availability.primaryLabel}<span aria-hidden="true">↗</span>
                </button>
                <a className="secondary-button" href="#product">See how Sticket works <span aria-hidden="true">↓</span></a>
                <span className="availability-pill">Private testing · iPhone first</span>
              </div>
            </div>
            <div className="hero-device" aria-label="Illustrative Sticket feed preview">
              <PhoneStage active="feed" compact />
            </div>
          </div>
          <div className="hero-marquee" aria-hidden="true">
            <span>LOG <i>·</i> REMEMBER <i>·</i> CONNECT <i>·</i> PLAN <i>·</i> </span>
            <span>LOG <i>·</i> REMEMBER <i>·</i> CONNECT <i>·</i> PLAN <i>·</i> </span>
          </div>
        </section>

        <section className="manifesto stub-section" aria-labelledby="manifesto-title">
          <div className="notch notch-left" aria-hidden="true" />
          <div className="notch notch-right" aria-hidden="true" />
          <div className="manifesto-inner layout-bound">
            <p className="eyebrow">THE MEMORY AFTER THE MOMENT</p>
            <h2 id="manifesto-title">A ticket gets you in.<br />A <span className="manifesto-accent">memory</span> brings you back.</h2>
            <div className="manifesto-copy">
              <p>Most concert apps stop at discovery. Most social apps flatten the night into another post.</p>
              <p>Sticket keeps the full arc: before, during, and long after the encore.</p>
            </div>
          </div>
        </section>

        <ProductStory />

        <section className="facts-section stub-section" aria-labelledby="facts-title">
          <div className="notch notch-left" aria-hidden="true" />
          <div className="notch notch-right" aria-hidden="true" />
          <div className="layout-bound">
            <div className="section-kicker"><span>PRODUCT FACTS</span><span>NO INVENTED METRICS</span></div>
            <h2 id="facts-title">What Sticket is,<br />precisely.</h2>
            <div className="facts-grid">
              {productFacts.map(([value, label, description]) => (
                <article key={label}>
                  <strong>{value}</strong>
                  <span>{label}</span>
                  <p>{description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="trust-section" id="trust" aria-labelledby="trust-title">
          <div className="layout-bound trust-layout">
            <div className="trust-heading">
              <p className="eyebrow">SHARE WITH INTENTION</p>
              <h2 id="trust-title">Your story.<br />Your call.</h2>
              <p>Specific controls, stated plainly. No blanket promises the product cannot prove.</p>
            </div>
            <ol className="trust-list">
              <li><span>01</span><div><b>Choose every memory’s audience.</b><p>Public, friends, or private — with separate controls for your profile, timeline, collection, and map.</p></div></li>
              <li><span>02</span><div><b>Public means deliberately shared.</b><p>A memory reaches the public web only when it is shared publicly and the profile allows it.</p></div></li>
              <li><span>03</span><div><b>Match contacts without keeping the address book.</b><p>Contact matching happens in the request; the API does not persist your uploaded contacts.</p></div></li>
              <li><span>04</span><div><b>{photoMatchTrust.title}</b><p>{photoMatchTrust.body}</p></div></li>
              <li><span>05</span><div><b>Take your history with you.</b><p>Export your logged shows as CSV, or delete your account and its connected data.</p></div></li>
              <li><span>06</span><div><b>No tracking on this page.</b><p>This proposal contains no analytics, advertising scripts, cookies, remote fonts, or data-collecting forms.</p></div></li>
            </ol>
          </div>
        </section>

        <section className="availability-section" id="availability" aria-labelledby="availability-title">
          <div className="availability-ticket">
            <div className="ticket-meta"><span>STICKET / PRIVATE ACCESS</span><span>IPHONE FIRST</span></div>
            <LiteralBrandMark className="availability-brand-mark" />
            <div>
              <p className="eyebrow">PRIVATE TESTING · IPHONE FIRST</p>
              <h2 id="availability-title">Coming soon.<br />No pretend waitlist.</h2>
              <p>The public App Store listing is not live. Until it is, there is no invented download link and no form collecting addresses.</p>
            </div>
            <a
              href={availability.detailHref}
              aria-label={availabilityAccessibleName(availability.detailLabel)}
            >
              {availability.detailLabel}<span aria-hidden="true">↗</span>
            </a>
          </div>
        </section>

        <section className="faq-section" id="faq" aria-labelledby="faq-title">
          <div className="layout-bound faq-layout">
            <div>
              <p className="eyebrow">THE DETAILS</p>
              <h2 id="faq-title">Good questions.<br />Straight answers.</h2>
            </div>
            <div className="faq-list">
              {faqs.map(({ question, answer }) => (
                <details key={question}>
                  <summary>{question}<span aria-hidden="true">+</span></summary>
                  <p>{answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>

      <StickyMobileCta onInviteRequest={openInviteDialog} />

      <footer className="site-footer" id="site-footer">
        <div className="layout-bound footer-main">
          <div className="footer-brand"><LiteralBrandMark className="compact-brand-mark" /><Wordmark /><p>Your concerts, kept alive.</p></div>
          <div className="footer-links"><span>EXPLORE</span><a href="#product">Product</a><a href="#trust">Trust</a><a href="#faq">FAQ</a></div>
          <div className="footer-links"><span>CONTACT</span><a href="mailto:support@sticket.in">support@sticket.in</a><a href="mailto:privacy@sticket.in">privacy@sticket.in</a></div>
          <div className="footer-status"><span>PREVIEW STATUS</span><p>Private testing · iPhone first. No analytics or data collection on this preview.</p></div>
        </div>
        <div className="layout-bound footer-bottom">
          <span>© 2026 STICKET</span>
          <span>Illustrative scenarios; names/data do not represent actual Sticket users or attendance.</span>
        </div>
      </footer>

      <InviteCodeDialog
        open={inviteDialogOpen}
        onClose={closeInviteDialog}
        returnFocusRef={inviteTriggerRef}
      />
    </>
  );
}

export default App;
